import { useQuery } from '@tanstack/react-query';
import type {
  CommsCostReport,
  FutureRevenueProjection,
  OccupancyReport,
  PaymentCostComposition,
  StaffKpiDashboard,
  TakingsReport,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS9 — the six server-computed reports for the console Reports + Money-admin surfaces (brief 09 §reports).
 * TanStack Query over the generated client, mirroring data/commission.ts → useStaffEarningsReport EXACTLY.
 *
 * MONEY HONESTY (ADR-0009, load-bearing): EVERY money figure here is the SERVER's. These hooks DISPLAY the
 * server's totals/VAT/projection/net — the UI never recomputes a projection, takings total, fee composition
 * or utilisation in JS. So every read carries `staleTime: 0` (a money figure is never served stale) and its
 * query key is DELIBERATELY NOT in PERSISTED_PREFIXES (never served from an offline replica).
 *
 * RBAC: reads are gated by the `enabled` arg so a non-privileged role never fires a 403-guaranteed request —
 * the comms-cost and payment-cost-composition endpoints are manager-only on the API; the caller passes the
 * isFloorManager gate (mirror of ChairRentPanel). The other four are visible to any reports:read principal
 * (a renter sees only their own row, RLS-scoped server-side).
 */

/**
 * The date range a report is read over. `from`/`to` are inclusive salon-local calendar days ("YYYY-MM-DD");
 * `tz` is the salon timezone (the report hooks pass it through, exactly like useStaffEarningsReport). This is
 * the SINGLE contract both UI builders consume — the DateRange control in features/reports/shared.tsx mints it.
 */
export interface ReportRange {
  from: string;
  to: string;
  tz?: string;
}

/** API horizon bounds (mirror of the reports route's ProjectionQuery min/max). */
const MIN_HORIZON_WEEKS = 1;
const MAX_HORIZON_WEEKS = 52;
const clampHorizon = (weeks: number) =>
  Math.min(MAX_HORIZON_WEEKS, Math.max(MIN_HORIZON_WEEKS, Math.round(weeks)));

/**
 * The HERO future-revenue projection: booked/confirmed priceQuoted by week & month, DISCOUNTED by the
 * derived/overridden no-show rate. This replaces the mis-sourced client-side projection.
 *
 * FORWARD-LOOKING by construction (the whole point of the report): the endpoint takes `from` + a forward
 * `horizonWeeks` (not a from/to pair), and the server scans `startAt >= from AND startAt < from + horizonWeeks*7d`.
 * We therefore DO NOT reuse the shared backward-looking last-30-days DateRange here — `range.from` must be
 * today (anchored forward by the caller's dedicated horizon control) and `horizonWeeks` is the explicit
 * forward horizon, NOT the inclusive span of a historical from/to window. The five other reports are
 * legitimately historical; this one looks ahead.
 */
export function useFutureRevenueProjection(
  range: ReportRange,
  horizonWeeks: number,
  enabled = true,
) {
  const weeks = clampHorizon(horizonWeeks);
  return useQuery({
    queryKey: qk.futureRevenue(range, weeks),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<FutureRevenueProjection> =>
      unwrap(
        await api.GET('/v1/reports/future-revenue', {
          params: {
            query: { from: range.from, horizonWeeks: weeks, tz: range.tz },
          },
        }),
      ),
  });
}

/** Succeeded-payment takings over a range, grouped by day/staff/service (gross/net/refunded/tips). */
export function useTakingsReport(
  range: ReportRange,
  groupBy: 'day' | 'staff' | 'service' = 'day',
  enabled = true,
) {
  return useQuery({
    queryKey: qk.takings(range, groupBy),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<TakingsReport> =>
      unwrap(
        await api.GET('/v1/reports/takings', {
          params: { query: { from: range.from, to: range.to, tz: range.tz, groupBy } },
        }),
      ),
  });
}

/** Comms invoice breakdown by channel & day + smart-routing savings. Manager-only on the API. */
export function useCommsCostReport(range: ReportRange, enabled = true) {
  return useQuery({
    queryKey: qk.commsCost(range),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<CommsCostReport> =>
      unwrap(
        await api.GET('/v1/reports/comms-cost', {
          params: { query: { from: range.from, to: range.to, tz: range.tz } },
        }),
      ),
  });
}

/** Chair occupancy/utilisation: booked vs available chair-minutes, by staff & day. */
export function useOccupancyReport(range: ReportRange, enabled = true) {
  return useQuery({
    queryKey: qk.occupancy(range),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<OccupancyReport> =>
      unwrap(
        await api.GET('/v1/reports/occupancy', {
          params: { query: { from: range.from, to: range.to, tz: range.tz } },
        }),
      ),
  });
}

/**
 * Per-staff KPI dashboard: completions/no-shows/revenue/avg-ticket/rebook/utilisation (+ commission/earnings).
 * REPLACES the old client-side per-chair cards — every figure is server-computed.
 */
export function useStaffKpiReport(range: ReportRange, enabled = true) {
  return useQuery({
    queryKey: qk.staffKpi(range),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<StaffKpiDashboard> =>
      unwrap(
        await api.GET('/v1/reports/staff-kpi', {
          params: { query: { from: range.from, to: range.to, tz: range.tz } },
        }),
      ),
  });
}

/** Payment-cost transparency: platform fee + Stripe interchange vs net (Overview 6.10). Manager-only. */
export function usePaymentCostComposition(range: ReportRange, enabled = true) {
  return useQuery({
    queryKey: qk.paymentCostComposition(range),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<PaymentCostComposition> =>
      unwrap(
        await api.GET('/v1/reports/payment-cost-composition', {
          params: { query: { from: range.from, to: range.to, tz: range.tz } },
        }),
      ),
  });
}
